import type { Config } from 'isomorphic-dompurify';

/**
 * DOMPurify configuration shared by everything that renders AI-generated HTML.
 *
 * SECURITY: Sanitizes all HTML output to prevent XSS attacks from malicious content in
 * iframe messages (event handlers, javascript: URLs, etc.)
 *
 * KaTeX-specific: Allows mathml elements and katex-* classes required by KaTeX output.
 * FORBID_TAGS/FORBID_ATTR block dangerous patterns like <script>, <iframe>, onerror, onload, etc.
 *
 * Lives in its own module so the question bank can re-sanitize before rendering without
 * pulling marked and katex (the reason the embed itself is lazily loaded) into its chunk.
 */
export const PURIFY_CONFIG: Config = {
  ALLOWED_TAGS: [
    // Text formatting
    'p', 'br', 'strong', 'em', 'b', 'i', 'u', 's', 'code', 'pre', 'span', 'div',
    // Lists
    'ul', 'ol', 'li',
    // Tables (IZ question stems carry data tables)
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    // Headings
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    // Links (href only, no javascript:)
    'a',
    // KaTeX MathML elements
    'math', 'annotation', 'semantics',
    'mrow', 'mi', 'mo', 'mn', 'msup', 'msub', 'mfrac', 'mover', 'munder',
    'mtable', 'mtr', 'mtd', 'mtext', 'mspace', 'merror', 'mstyle', 'munderover',
    // KaTeX display
    'span',
    // SVG (used by KaTeX)
    'svg', 'path', 'g', 'rect', 'circle', 'line', 'polyline', 'polygon',
  ],
  ALLOWED_ATTR: [
    'class', 'style', 'aria-hidden', 'aria-label', 'id',
    // KaTeX-specific
    'data-katex', 'data-pgfd-equation',
    // SVG
    'viewBox', 'd', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin',
    'points', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'width', 'height',
    // Links (no javascript:)
    'href',
    // Table cells
    'colspan', 'rowspan',
  ],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'button', 'select'],
  FORBID_ATTR: [
    'onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onfocus', 'onblur',
    'onchange', 'onsubmit', 'onkeydown', 'onkeyup', 'onkeypress', 'onscroll',
  ],
  ALLOW_DATA_ATTR: false,
  KEEP_CONTENT: true,
};
